import { useContext, useEffect, useState } from 'react';
import { IPaymentTask } from '../../../interfaces/delivery-company/tasks';
import { API_ENDPOINT } from '../../../configurations/global.config';
import axios from 'axios';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { timestampedMessage } from '../../common/multi-package-scanner/multi-package-scanner';
import { DeliveryBatchState, PaymentTaskState } from '../../../enums/tasks';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import SnackBar from '../../common/snack-bar/snack-bar';
import CustomDialog from '../../common/custom-dialog/custom-dialog';
import { isMobile, isTablet } from 'react-device-detect';
import { ClearIcon } from '@mui/x-date-pickers';
import { pickupTaskToQRcodeID } from '../../../utils/pickup-tasks';
import ItemsCount from '../../common/labels-input/items-count/items-count';
import QRCodeBox from '../../common/qrcode-box/qrcode-box';
import { merchantInvoiceTaskToQRcodeID } from '../../../utils/merchant-invoices';
import { IMerchantOrdersInvoice } from '../../../interfaces/common/merchant-invoices';
import { showPaymentState } from '../../common/styles/order-states.style';
import { showPaymentTaskState } from '../../common/styles/payment0task.styles';
import { collectInvoice } from '../common/collect_invoice';
import { useNavigate } from 'react-router-dom';
import {
  FINISHED,
  PENDING,
  TASK_STATE_CATEGORY,
} from '../driver-payment-tasks/filter_utils';
import { MerchantWithIcon } from '../../common/styles/merchant-with-icon';
import AddressWithIcon from '../../common/styles/address-with-icon';
import StaticGoogleMap from '../../common/static-google-map/static-google-map';
import { extractMapsPlaceLink } from '../../../utils/locations';
import { NotificationTemplate } from '../../../interfaces/delivery-company/orders';
import AddNotesDialog from '../common/add-notes-dialog';
import MarkAsFailedDialog from '../common/mark-as-failed-dialog';
import ConfirmPaymentDialog from '../driver-payment-tasks/confirm-payment-dialog';
import SendMessageDialog from '../common/send-message-dialog/send-message-dialog';
import { green, red } from '@mui/material/colors';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import { useSnackBar } from '../../../contexts/snackbar';
import { buildResolveTemplateArgsForPayment } from '../../delivery-company-components/settings/notification-manager/utils';

interface PaymentTaskQuickScanDialogProps {
  id: string;
  onClose: () => void;
}
function PaymentTaskhQuickScanDialog({
  id,
  onClose,
}: PaymentTaskQuickScanDialogProps) {
  const [paymentTask, setPaymentTask] = useState<IPaymentTask>();
  const [snackBar, setSnackBar] = useState<timestampedMessage>();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [isPerformingAction, setIsPerformingAction] = useState(false);
  const [whatsAppDialogOpen, setWhatsAppDialogOpen] = useState(false);
  const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);
  const [markAsFailedDialogOpen, setMarkAsFailedDialogOpen] = useState(false);
  const [secretDialogTask, setSecretDialogTask] = useState<IPaymentTask | null>(
    null
  );
  const { showMessage } = useSnackBar();

  const { user, config } = useContext(AuthContext);

  useEffect(() => {
    const fetchTask = async () => {
      let payment_tasks: IPaymentTask[] = [];
      setIsLoading(true);
      try {
        const response = await axios.get<{
          results: IPaymentTask[];
          count: number;
        }>(`${API_ENDPOINT}/api/v1/payment-tasks/?invoice=${id}`, config);
        payment_tasks = response.data.results;
        if (response.data.count === 0) {
          throw new Error('No tasks found');
        }
      } catch (e) {
        setSnackBar({
          timestamp: Date.now(),
          level: 'error',
          content: 'No payment task found',
        });
        setIsLoading(false);
        return;
      }

      const last_payment_task = payment_tasks[0];

      if (
        last_payment_task.driver != null &&
        parseInt(last_payment_task.driver.id) != user?.id
      ) {
        setSnackBar({
          timestamp: Date.now(),
          level: 'error',
          content: `This task is associated to driver ${last_payment_task.driver.name}`,
        });
        setIsLoading(false);
        return;
      }

      setPaymentTask(last_payment_task);

      setIsLoading(false);
    };
    fetchTask();
  }, []);

  const handleSendWhatAppMessage = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/payment-tasks/notify/`;
    const requestData = {
      message: template,
      task_ids: [paymentTask?.id],
    };

    return axios.post(endpoint, requestData, config);
  };

  const handleMarkAsFailedTasks = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/payment-tasks/bulk-update/`;
    const requestData = [
      {
        id: paymentTask?.id,
        notes: [template],
        status: PaymentTaskState.AWAITING_RETURN,
      },
    ];

    return axios.patch(endpoint, requestData, config);
  };

  const handleAddNote = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/payment-tasks/bulk-update/`;
    const requestData = [
      {
        id: paymentTask?.id,
        notes: [template],
      },
    ];
    console.log(requestData);
    return axios.patch(endpoint, requestData, config);
  };

  const handleStartTaskUtil = async (id: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/payment-tasks/bulk-update/`;
    if (!paymentTask) {
      showMessage('An error occurred', 'error');
      return false;
    }
    const requestData = [
      {
        id: id,
        status: PaymentTaskState.IN_PROGRESS,
      },
    ];
    setIsPerformingAction(true);
    try {
      await axios.patch(endpoint, requestData, config);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleStartTask = async () => {
    if (!paymentTask) {
      showMessage('An error occurred', 'error');
      return;
    }
    setIsPerformingAction(true);
    if (await handleStartTaskUtil(paymentTask.id)) {
      showMessage('Task started');
      setPaymentTask({ ...paymentTask, status: PaymentTaskState.IN_PROGRESS });
    } else {
      showMessage('Failed to start task', 'error');
    }
    setIsPerformingAction(false);
  };

  return (
    <>
      {isLoading ? (
        <div style={{ textAlign: 'center', width: '100%' }}>
          <CircularProgress />{' '}
        </div>
      ) : paymentTask != undefined ? (
        <CustomDialog open={true} onClose={onClose} fullScreen={(isMobile && !isTablet)}>
          <DialogContent>
            <Typography variant="h3" textAlign="center">
              Invoice #{id}
            </Typography>
            <QRCodeBox
              value={merchantInvoiceTaskToQRcodeID({
                id: id,
              } as IMerchantOrdersInvoice)}
              showValue
              centered
              size={100}
            />

            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
              }}
            >
              <IconButton onClick={onClose}>
                <ClearIcon color="primary" fontSize="large" />
              </IconButton>
            </div>

            <div style={{ textAlign: 'center', width: '100%' }}>
              {showPaymentTaskState(paymentTask.status)}
            </div>

            {paymentTask.status === PaymentTaskState.IN_PROGRESS && (
              <>
                <div
                  style={{
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'center',
                  }}
                  onClick={() => {
                    setSecretDialogTask(paymentTask);
                  }}
                >
                  <Chip
                    icon={<CurrencyExchangeIcon sx={{ color: green[500] }} />}
                    label={'Pay Invoice'}
                    size="medium"
                  ></Chip>
                </div>
              </>
            )}

            {[
              PaymentTaskState.SCHEDULED,
              PaymentTaskState.OUT_FOR_PAYMENT,
              PaymentTaskState.IN_PROGRESS,
            ].includes(paymentTask.status) && <hr />}

            <div style={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
              {isPerformingAction ? (
                <CircularProgress />
              ) : (
                <>
                  {paymentTask.status == PaymentTaskState.SCHEDULED && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={async () => {
                        setIsPerformingAction(true);
                        await collectInvoice(
                          paymentTask.id,
                          config,
                          () =>
                            setPaymentTask({
                              ...paymentTask,
                              status: PaymentTaskState.OUT_FOR_PAYMENT,
                            }),
                          setSnackBar
                        );
                        setIsPerformingAction(false);
                      }}
                    >
                      Collect Invoice
                    </Button>
                  )}
                  {paymentTask.status == PaymentTaskState.OUT_FOR_PAYMENT && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleStartTask()}
                    >
                      Start
                    </Button>
                  )}

                  {[
                    PaymentTaskState.IN_PROGRESS,
                    PaymentTaskState.OUT_FOR_PAYMENT,
                  ].includes(paymentTask.status) && (
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => setWhatsAppDialogOpen(true)}
                      >
                        Notify
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => setAddNoteDialogOpen(true)}
                      >
                        Add note
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => setMarkAsFailedDialogOpen(true)}
                      >
                        Fail
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>

            <hr />

            <div style={{ color: red[400] }}>
              {(paymentTask.notes ?? []).join(' | ')}
            </div>

            <MerchantWithIcon
              merchant={paymentTask.merchant}
              phone_number={paymentTask.merchant_location?.contact_phone}
            />
            <AddressWithIcon address={paymentTask.merchant_location} />
            <br />
            <StaticGoogleMap
              mapPath={extractMapsPlaceLink(paymentTask.merchant_location)}
            />

            {whatsAppDialogOpen && (
              <SendMessageDialog
                setSnackBarResult={(t?: timestampedMessage) => setSnackBar(t)}
                onSubmit={handleSendWhatAppMessage}
                onClose={() => setWhatsAppDialogOpen(false)}
                stage={'Payment'}
                onSuccess={() => {
                  if (paymentTask.status === PaymentTaskState.OUT_FOR_PAYMENT)
                    setPaymentTask({
                      ...paymentTask,
                      status: PaymentTaskState.IN_PROGRESS,
                    });
                }}
                locallyResolvedTargets={[
                  {
                    id: paymentTask.id,
                    label: `Payment Task #${paymentTask.id}`,
                    phone_number:
                      paymentTask.merchant_location?.contact_phone ?? '',
                    resolveTemplateArgs: buildResolveTemplateArgsForPayment(
                      paymentTask,
                      user?.name ?? '',
                      user?.organisation?.org_id ?? 'ERROR'
                    ),
                    alreadyStarted:
                      paymentTask.status != PaymentTaskState.OUT_FOR_PAYMENT,
                  },
                ]}
                startTask={handleStartTaskUtil}
              />
            )}
            {secretDialogTask && (
              <ConfirmPaymentDialog
                onClose={() => setSecretDialogTask(null)}
                task={secretDialogTask}
                onSuccess={() => {
                  /**nothing to do */
                }}
              />
            )}
            {addNoteDialogOpen && (
              <AddNotesDialog
                setSnackBarResult={(t?: timestampedMessage) => setSnackBar(t)}
                onSubmit={handleAddNote}
                onSuccess={(m: string) =>
                  setPaymentTask({
                    ...paymentTask,
                    notes: paymentTask.notes ? [...paymentTask.notes, m] : [m],
                  })
                }
                onClose={() => setAddNoteDialogOpen(false)}
              />
            )}
            {markAsFailedDialogOpen && (
              <MarkAsFailedDialog
                onSubmit={handleMarkAsFailedTasks}
                onClose={() => setMarkAsFailedDialogOpen(false)}
                failure_reasons={[
                  'Shop closed',
                  'Wrong amount',
                  'Wrong invoice',
                  'Person absent',
                  'Invoice lost',
                ]}
                setSnackBarResult={setSnackBar}
                onSuccess={(r) =>
                  setPaymentTask({
                    ...paymentTask,
                    status: PaymentTaskState.AWAITING_RETURN,
                    notes: paymentTask.notes ? [...paymentTask.notes, r] : [r],
                  })
                }
              />
            )}
          </DialogContent>
        </CustomDialog>
      ) : (
        <Alert severity="error">Payment task not found</Alert>
      )}
      {snackBar && snackBar.content && (
        <SnackBar
          message={snackBar.content}
          level={snackBar.level}
          key={snackBar.timestamp}
        />
      )}
    </>
  );
}

export default PaymentTaskhQuickScanDialog;
